<template>
  <section class="hero-carousel">
    <swiper :modules="modules" slides-per-view="1" :loop="true" :autoplay="autoplayConfig" :pagination="paginationConfig" :navigation="true" :speed="350" @swiper="onSwiper" @slideChange="onSlideChange">
      <swiper-slide v-for="(slide, index) in slides" :key="index" class="carousel-slide" :style="{ background: slide.bg }">
        <div class="slide-icon">
          <div class="sfwww a1 front"></div>
          <div class="sfwww a1 back"></div>
        </div>
        <div class="slide-content">
          <h1 v-if="slide.title && slide.title.zh" class="slide-title">{{ slide.title && slide.title[locale] }}</h1>
          <p v-if="slide.description && slide.description.zh" class="slide-description">{{ slide.description[locale] }}</p>
          <div class="slide-actions">
            <NuxtLink v-if="slide.primaryLink" :to="slide.primaryLink" class="btn btn-primary">
              {{ slide.primaryText }}
            </NuxtLink>
            <NuxtLink v-if="slide.secondaryLink" :to="slide.secondaryLink" class="btn btn-outline">
              {{ slide.secondaryText }}
            </NuxtLink>
          </div>
        </div>
      </swiper-slide>
    </swiper>

    <!-- 滚动指示器 -->
    <div class="hero-scroll-indicator">
      <div class="scroll-arrow"></div>
    </div>
  </section>
</template>

<script setup>
  import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
  import { Swiper, SwiperSlide } from 'swiper/vue'
  import { Autoplay, Navigation, Pagination, Scrollbar, A11y } from 'swiper/modules'
  import 'swiper/css/bundle'

  // 定义 props
  const props = defineProps({
    slides: {
      type: Array,
      required: true,
      default: () => []
    },
    autoplay: {
      type: [Boolean, Object],
      default: false
    },
    interval: {
      type: Number,
      default: 8000
    }
  })

  // 定义 emits（如果需要）
  const emit = defineEmits(['slide-change'])

  // 响应式数据
  const swiperInstance = ref(null)
  const currentSlideIndex = ref(0)
  const locale = ref('zh') // 根据实际情况设置，可以从 useI18n() 获取

  // 计算属性
  const autoplayConfig = computed(() => {
    if (props.autoplay === false) return false
    return {
      delay: props.interval,
      disableOnInteraction: false,
      pauseOnMouseEnter: true
    }
  })

  const paginationConfig = computed(() => ({
    clickable: true,
    dynamicBullets: true,
    renderBullet: (index, className) => {
      return `<span class="${className}" aria-label="第${index + 1}张幻灯片"></span>`
    }
  }))

  // 模块配置
  const modules = [Autoplay, Navigation, Pagination, Scrollbar, A11y]

  // 方法
  const onSwiper = swiper => {
    swiperInstance.value = swiper
    // console.log('Swiper instance:', swiper)
  }

  const onSlideChange = e => {
    currentSlideIndex.value = e.activeIndex
    // console.log('Current slide:', e.activeIndex)
    emit('slide-change', e.activeIndex)
  }

  const handleAction = action => {
    if (action.handler) {
      action.handler()
    } else if (action.link) {
      window.location.href = action.link
    }
  }

  // 提供给父组件调用的方法
  const nextSlide = () => {
    swiperInstance.value?.slideNext()
  }

  const prevSlide = () => {
    swiperInstance.value?.slidePrev()
  }

  const goToSlide = index => {
    swiperInstance.value?.slideTo(index)
  }

  // 生命周期
  onMounted(() => {
    // console.log('HeroCarousel mounted')
  })

  onBeforeUnmount(() => {
    // 清理工作
    if (swiperInstance.value) {
      swiperInstance.value.destroy(true, true)
    }
  })

  // 暴露给模板的方法和属性
  defineExpose({
    nextSlide,
    prevSlide,
    goToSlide,
    currentSlideIndex
  })
</script>

<style scoped lang="less">
  .hero-carousel {
    position: relative;
    height: 100vh;
    overflow: hidden;
  }

  .hero-swiper {
    height: 100%;
    width: 100%;
  }

  .carousel-slide {
    height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    position: relative;
  }

  .slide-content {
    position: relative;
    z-index: 2;
    text-align: center;
    max-width: 800px;
    margin: 0 auto;
    padding: var(--space-8);
    color: white;
  }

  .slide-icon {
    margin-top: 50vh;
    font-size: 6.1em;
    margin-bottom: 20px;
    position: relative;
    color: #fff;
    .front {
      font-size: inherit;
      mix-blend-mode: overlay;
      top: 0;
    }
    .back{
      position: absolute;
      font-size: inherit;
      top: 1px;
      left: 2px;
      opacity: 0.25;
    }
  }
  .slide-title {
    font-size: var(--text-5xl);
    font-weight: 700;
    margin-bottom: var(--space-6);
    line-height: 1.2;
    opacity: 0;
    transform: translateY(30px);
    animation: slideUpFadeIn 0.8s ease-out 0.2s forwards;
  }

  .slide-description {
    font-size: var(--text-xl);
    margin-bottom: var(--space-8);
    opacity: 0.9;
    line-height: 1.6;
    opacity: 0;
    transform: translateY(30px);
    animation: slideUpFadeIn 0.8s ease-out 0.4s forwards;
  }

  .slide-actions {
    display: flex;
    justify-content: center;
    gap: var(--space-4);
    flex-wrap: wrap;
    opacity: 0;
    transform: translateY(30px);
    animation: slideUpFadeIn 0.8s ease-out 0.6s forwards;
  }

  .btn {
    padding: var(--space-3) var(--space-6);
    border-radius: var(--radius-md);
    font-weight: 600;
    text-decoration: none;
    display: inline-block;
    transition: all 0.3s ease;
    border: none;
    cursor: pointer;
  }

  .btn.primary {
    background-color: var(--color-primary);
    color: white;
  }

  .btn.secondary {
    background-color: transparent;
    color: white;
    border: 2px solid white;
  }

  .btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  @keyframes slideUpFadeIn {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* 自定义 Swiper 样式 */
  :deep(.swiper-pagination) {
    bottom: var(--space-8) !important;
  }

  :deep(.swiper-pagination-bullet) {
    width: 8px;
    height: 8px;
    background: rgba(255, 255, 255, 0.5);
    opacity: 1;
    transition: all 0.3s ease;
  }

  :deep(.swiper-pagination-bullet-active) {
    background: white;
  }

  :deep(.swiper-button-prev) {
    left: var(--space-6);
    color: #fff;
    opacity: 0.6;
  }

  :deep(.swiper-button-next) {
    right: var(--space-6);
    color: #fff;
    opacity: 0.6;
  }

  :deep(.swiper-button-prev:hover),
  :deep(.swiper-button-next:hover) {
    opacity: 1;
  }

  /* Scroll Indicator */
  .hero-scroll-indicator {
    position: absolute;
    bottom: var(--space-16);
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
    animation: bounce 2s infinite;
  }

  .scroll-arrow {
    width: 24px;
    height: 24px;
    border: 2px solid white;
    border-top: none;
    border-left: none;
    transform: rotate(45deg);
  }

  @keyframes bounce {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateX(-50%) translateY(0);
    }
    40% {
      transform: translateX(-50%) translateY(-10px);
    }
    60% {
      transform: translateX(-50%) translateY(-5px);
    }
  }

  /* 响应式设计 */
  @media (max-width: 768px) {
    .hero-carousel {
      height: 80vh;
    }

    .hero-swiper {
      height: 80vh;
    }

    .carousel-slide {
      height: 80vh;
    }
    .slide-icon {
      font-size: 4.5rem;
    }
    .slide-title {
      font-size: var(--text-3xl);
    }

    .slide-description {
      font-size: var(--text-lg);
    }

    .slide-actions {
      flex-direction: column;
      align-items: center;
    }

    :deep(.swiper-button-prev),
    :deep(.swiper-button-next) {
      display: none;
    }

    :deep(.swiper-pagination) {
      bottom: var(--space-6) !important;
    }

    .hero-scroll-indicator {
      bottom: var(--space-12);
    }
  }

  @media (max-width: 480px) {
    .hero-carousel {
      height: 70vh;
    }

    .hero-swiper {
      height: 70vh;
    }

    .carousel-slide {
      height: 70vh;
    }
    .slide-icon {
      font-size: 3.5rem;
      text-shadow: 2px 2px 2px rgba(255, 255, 255, 0.3), -1px -1px 1px rgba(0, 0, 0, 0.1);
      opacity: 0.65;
    }
    .slide-title {
      font-size: var(--text-2xl);
    }

    .slide-content {
      padding: var(--space-6);
    }

    :deep(.swiper-pagination) {
      bottom: var(--space-4) !important;
    }

    .hero-scroll-indicator {
      bottom: var(--space-8);
    }
  }
</style>
